import { useState, useRef, useEffect } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import ReactMarkdown from 'react-markdown'
import BuildLog from '../components/BuildLog'
import VersionHistory from '../components/VersionHistory'

const OPENCLAW_TOKEN = 'fd6aa805e3747416b6e83eb3e66bfbc7969561eb903c22c3'
const API_URL = 'https://api.dragon-claw.com'

// ALL calls go through this agent - it handles model routing internally
const GATEWAY_MODEL = 'openclaw/dragonclaw-web'

// Visual model picker - shown to user but all route through the gateway agent
// To enable direct routing, create separate OpenClaw agents per model
const MODELS = [
  { id: 'auto', label: 'Auto', icon: '🐉', description: 'Smart routing' },
  { id: 'minimax', label: 'MiniMax', icon: '⚡', description: 'Best quality' },
  { id: 'minimax-fast', label: 'MiniMax Fast', icon: '🚀', description: 'Fastest' },
  { id: 'gemini-pro', label: 'Gemini 2.5 Pro', icon: '🧠', description: 'Most powerful' },
  { id: 'gemini-flash', label: 'Gemini 2.5 Flash', icon: '✨', description: 'Fast + smart' },
  { id: 'gemini-2', label: 'Gemini 2.0', icon: '🔮', description: 'Balanced' },
  { id: 'qwen-coder', label: 'Qwen Coder', icon: '💻', description: 'Free · coding' },
  { id: 'gpt-120b', label: 'GPT OSS 120B', icon: '🤖', description: 'Free · large' },
]

// Model preference hints added to system prompt
const MODEL_HINTS = {
  'auto': '',
  'minimax': 'Use MiniMax as the primary model for this request.',
  'minimax-fast': 'Use MiniMax highspeed as the primary model for this request.',
  'gemini-pro': 'Use Gemini 2.5 Pro as the primary model for this request.',
  'gemini-flash': 'Use Gemini 2.5 Flash as the primary model for this request.',
  'gemini-2': 'Use Gemini 2.0 Flash as the primary model for this request.',
  'qwen-coder': 'Use Qwen Coder as the primary model for this request.',
  'gpt-120b': 'Use GPT OSS 120B as the primary model for this request.',
}

const PREVIEW_SIZES = [
  { id: 'desktop', label: 'Desktop', icon: '🖥', maxWidth: '100%' },
  { id: 'tablet', label: 'Tablet', icon: '⬜', maxWidth: '768px' },
  { id: 'mobile', label: 'Mobile', icon: '📱', maxWidth: '375px' },
]

const BUILD_STEPS = {
  build: [
    { type: 'planning', text: 'Reading your request...' },
    { type: 'designing', text: 'Planning the structure...' },
    { type: 'designing', text: 'Designing the layout...' },
    { type: 'coding', text: 'Writing the code...' },
    { type: 'building', text: 'Building components...' },
    { type: 'styling', text: 'Applying styles...' },
    { type: 'testing', text: 'Finalizing output...' },
  ],
  discuss: [
    { type: 'planning', text: 'Analyzing your question...' },
    { type: 'writing', text: 'Thinking...' },
    { type: 'writing', text: 'Writing response...' },
  ]
}

function generateSuggestions(content, userMsg) {
  const text = (content + ' ' + userMsg).toLowerCase()
  if (text.includes('timer') || text.includes('pomodoro') || text.includes('clock'))
    return ['Add alarm sound effects', 'Add progress history log', 'Add keyboard shortcuts']
  if (text.includes('dashboard') || text.includes('analytics') || text.includes('chart'))
    return ['Add export to CSV', 'Add date range filter', 'Add real-time refresh']
  if (text.includes('game') || text.includes('score') || text.includes('player'))
    return ['Add sound effects', 'Add high score board', 'Add mobile touch controls']
  if (text.includes('form') || text.includes('login') || text.includes('signup'))
    return ['Add form validation', 'Add loading spinner', 'Add success animation']
  if (text.includes('portfolio') || text.includes('landing') || text.includes('hero'))
    return ['Add contact form', 'Add scroll animations', 'Make it mobile responsive']
  if (text.includes('crypto') || text.includes('finance') || text.includes('stock'))
    return ['Add price alert system', 'Add portfolio calculator', 'Add candlestick chart']
  if (text.includes('todo') || text.includes('task') || text.includes('list'))
    return ['Add drag to reorder', 'Add due dates', 'Add priority levels']
  if (text.includes('weather') || text.includes('forecast'))
    return ['Add 7-day forecast', 'Add location search', 'Add animated weather icons']
  if (text.includes('fitness') || text.includes('workout') || text.includes('health'))
    return ['Add progress charts', 'Add workout timer', 'Add calorie tracker']
  if (text.includes('music') || text.includes('player') || text.includes('audio'))
    return ['Add visualizer', 'Add playlist support', 'Add keyboard controls']
  return ['Add dark mode toggle', 'Make it mobile responsive', 'Add smooth animations']
}

function extractCode(text) {
  if (!text) return { code: null, lang: null }
  const matches = [...text.matchAll(/```(\w*)\n([\s\S]*?)```/g)]
  if (!matches.length) {
    if (text.includes('<!DOCTYPE') || text.includes('<html')) return { code: text, lang: 'html' }
    return { code: null, lang: null }
  }
  const html = matches.find(m => ['html', 'svg'].includes(m[1]) || m[2].includes('<!DOCTYPE') || m[2].includes('<html'))
  if (html) return { code: html[2], lang: 'html' }
  const last = matches[matches.length - 1]
  return { code: last[2], lang: last[1] || 'txt' }
}

function getProjectSlug(name) {
  return (name || 'myapp').toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')
}

export default function Editor() {
  const { id: projectId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, isPro } = useAuth()

  const [project, setProject] = useState(null)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [codeOutput, setCodeOutput] = useState('')
  const [codeLang, setCodeLang] = useState('html')
  const [tab, setTab] = useState('preview')
  const [previewKey, setPreviewKey] = useState(0)
  const [previewSize, setPreviewSize] = useState('desktop')
  const [mode, setMode] = useState(searchParams.get('mode') || 'build')
  const [selectedModel, setSelectedModel] = useState('auto')
  const [showModelPicker, setShowModelPicker] = useState(false)
  const [buildSteps, setBuildSteps] = useState([])
  const [currentStatus, setCurrentStatus] = useState('')
  const [versionNumber, setVersionNumber] = useState(0)
  const [showPublish, setShowPublish] = useState(false)
  const [copied, setCopied] = useState(false)
  const [activeModel, setActiveModel] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [visibility, setVisibility] = useState('public')
  const [savingVisibility, setSavingVisibility] = useState(false)
  const [urlCopied, setUrlCopied] = useState(false)
  const messagesEndRef = useRef(null)
  const textareaRef = useRef(null)
  const stepIntervalRef = useRef(null)

  useEffect(() => {
    loadProject()
    const initialPrompt = searchParams.get('prompt')
    if (initialPrompt) setTimeout(() => sendMessage(initialPrompt), 800)
  }, [projectId])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  async function loadProject() {
    const { data: proj } = await supabase.from('projects').select('*').eq('id', projectId).single()
    if (!proj) { navigate('/projects'); return }
    setProject(proj)
    setVisibility(proj.visibility || 'public')
    const { data: msgs } = await supabase.from('project_messages').select('*').eq('project_id', projectId).order('created_at', { ascending: true })
    setMessages(msgs || [])
    const { data: file } = await supabase.from('project_files').select('*').eq('project_id', projectId).eq('filename', 'index.html').single()
    if (file?.content) { setCodeOutput(file.content); setCodeLang('html') }
    const { data: versions } = await supabase.from('project_versions').select('version_number').eq('project_id', projectId).order('version_number', { ascending: false }).limit(1).single()
    setVersionNumber(versions?.version_number || 0)
  }

  function startBuildAnimation(msgMode) {
    const steps = BUILD_STEPS[msgMode] || BUILD_STEPS.build
    let idx = 0
    setBuildSteps([{ ...steps[0], done: false }])
    setCurrentStatus(steps[0].text)
    stepIntervalRef.current = setInterval(() => {
      idx++
      if (idx < steps.length) {
        setBuildSteps(prev => [...prev.slice(0, -1).map(s => ({ ...s, done: true })), { ...steps[idx], done: false }])
        setCurrentStatus(steps[idx].text)
      }
    }, 3500)
  }

  function stopBuildAnimation() {
    if (stepIntervalRef.current) { clearInterval(stepIntervalRef.current); stepIntervalRef.current = null }
    setBuildSteps(prev => prev.map(s => ({ ...s, done: true })))
    setCurrentStatus('')
    setTimeout(() => setBuildSteps([]), 3000)
  }

  async function saveVersion(content, msg) {
    const newV = versionNumber + 1
    setVersionNumber(newV)
    await supabase.from('project_versions').insert({ project_id: projectId, version_number: newV, content, message: msg.slice(0, 80) })
    await supabase.from('project_files').upsert({ project_id: projectId, filename: 'index.html', content, language: 'html', updated_at: new Date().toISOString() }, { onConflict: 'project_id,filename' })
    await supabase.from('projects').update({ updated_at: new Date().toISOString() }).eq('id', projectId)
  }

  async function sendMessage(text) {
    const msg = (text || input).trim()
    if (!msg || loading) return

    setSuggestions([])
    const userMsg = { id: Date.now(), role: 'user', content: msg, mode, created_at: new Date().toISOString() }
    setMessages(prev => [...prev, userMsg])
    setInput(''); setLoading(true)
    if (textareaRef.current) textareaRef.current.style.height = 'auto'

    startBuildAnimation(mode)
    localStorage.setItem('dc_pending_job', JSON.stringify({ msg, projectId, ts: Date.now() }))
    await supabase.from('project_messages').insert({ project_id: projectId, user_id: user.id, role: 'user', content: msg, mode })

    const history = messages.slice(-12).map(m => ({ role: m.role, content: m.content }))
    history.push({ role: 'user', content: msg })

    const modelHint = MODEL_HINTS[selectedModel] || ''
    const currentModelLabel = MODELS.find(m => m.id === selectedModel)?.label || 'Auto'
    setActiveModel(currentModelLabel)

    const systemPrompt = mode === 'discuss'
      ? `You are DRAGONclaw, an AI coding advisor. Help the user plan their app. Be conversational and helpful. ${modelHint}`
      : `You are DRAGONclaw, an elite AI coding assistant on dragon-claw.com.
${modelHint}
ABSOLUTE RULES:
1. NEVER save files or use file system tools.
2. ALWAYS output the COMPLETE code directly in your response in a fenced code block.
3. For HTML/CSS/JS: use \`\`\`html and include ALL code inline - one complete self-contained file.
4. Output code FIRST, explanation AFTER.
5. The code renders live in a preview panel - it MUST be 100% self-contained.
6. Style preference: ${project?.style || 'dark'} theme.

NEVER say "Built file.html" or "Save as" or "Open in browser". Just output the complete code.`

    let data = null
    let attempts = 0

    while (attempts < 3) {
      try {
        const controller = new AbortController()
        const timeout = setTimeout(() => controller.abort(), 90000)
        const res = await fetch(`${API_URL}/v1/chat/completions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${OPENCLAW_TOKEN}` },
          body: JSON.stringify({ model: GATEWAY_MODEL, messages: [{ role: 'system', content: systemPrompt }, ...history], stream: false, max_tokens: 8000 }),
          signal: controller.signal,
        })
        clearTimeout(timeout)
        const responseText = await res.text()
        if (!res.ok) throw new Error(`http:${res.status}`)
        if (responseText.startsWith('<') || responseText.startsWith('error code:')) throw new Error('gateway_error')
        const parsed = JSON.parse(responseText)
        if (parsed.error) throw new Error(`api:${parsed.error.message}`)
        if (!parsed.choices?.[0]?.message?.content) throw new Error('empty')
        data = parsed
        break
      } catch (err) {
        attempts++
        console.warn(`Attempt ${attempts} failed:`, err.message)
        if (attempts < 3) {
          setCurrentStatus(`Retrying... (${attempts}/3)`)
          await new Promise(r => setTimeout(r, 2000))
        }
      }
    }

    if (!data) {
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', content: 'The server is not responding. Please check your connection and try again.', created_at: new Date().toISOString() }])
      localStorage.removeItem('dc_pending_job')
      stopBuildAnimation(); setLoading(false); return
    }

    const content = data.choices[0].message.content
    const reportedModel = data.model || GATEWAY_MODEL
    setActiveModel(reportedModel.split('/').pop())

    const { code, lang } = extractCode(content)
    if (code) {
      setCodeOutput(code); setCodeLang(lang || 'html')
      setTab('preview'); setPreviewKey(k => k + 1)
      await saveVersion(code, msg)
    }

    const { data: saved } = await supabase.from('project_messages').insert({ project_id: projectId, user_id: user.id, role: 'assistant', content, mode }).select().single()
    setMessages(prev => [...prev, saved || { id: Date.now() + 1, role: 'assistant', content, created_at: new Date().toISOString() }])
    localStorage.removeItem('dc_pending_job')

    if (mode === 'build' && code) setSuggestions(generateSuggestions(content, msg))

    stopBuildAnimation(); setLoading(false)
  }

  async function toggleVisibility() {
    const newVis = visibility === 'public' ? 'private' : 'public'
    setSavingVisibility(true)
    await supabase.from('projects').update({ visibility: newVis }).eq('id', projectId)
    setVisibility(newVis); setProject(prev => ({ ...prev, visibility: newVis }))
    setSavingVisibility(false)
  }

  async function downloadZip() {
    if (!codeOutput) return
    if (!window.JSZip) {
      await new Promise((res, rej) => {
        const s = document.createElement('script')
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js'
        s.onload = res; s.onerror = rej; document.head.appendChild(s)
      })
    }
    const zip = new window.JSZip()
    zip.file('index.html', codeOutput)
    zip.file('netlify.toml', '[build]\n  publish = "."\n')
    zip.file('README.md', `# ${project?.name || 'DRAGONclaw Project'}\n\nBuilt with [DRAGONclaw](https://dragon-claw.com)\n`)
    const blob = await zip.generateAsync({ type: 'blob' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url
    a.download = `${(project?.name || 'project').replace(/\s+/g, '-').toLowerCase()}.zip`
    a.click(); URL.revokeObjectURL(url)
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage() }
  }

  const currentModelInfo = MODELS.find(m => m.id === selectedModel) || MODELS[0]
  const projectSlug = getProjectSlug(project?.name)
  const publishedUrl = `https://${projectSlug}.dragon-claw.com`
  const currentPreviewSize = PREVIEW_SIZES.find(s => s.id === previewSize) || PREVIEW_SIZES[0]

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)', overflow: 'hidden' }}>
      {/* Top bar */}
      <div style={{ height: 48, borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', padding: '0 14px', gap: 12, flexShrink: 0 }}>
        <div onClick={() => navigate('/projects')} style={{ fontFamily: 'var(--font-disp)', fontSize: 20, color: 'var(--fire)', cursor: 'pointer', letterSpacing: 2 }}>🐉</div>
        <div style={{ width: 1, height: 20, background: 'var(--border)' }} />
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text)', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {project?.name || 'Loading...'}
        </div>

        <div style={{ display: 'flex', gap: 4, background: 'var(--panel)', borderRadius: 20, padding: '3px 4px' }}>
          {['build', 'discuss'].map(m => (
            <button key={m} onClick={() => setMode(m)}
              style={{ padding: '3px 12px', background: mode === m ? 'var(--fire)' : 'transparent', border: 'none', borderRadius: 16, color: mode === m ? '#fff' : 'var(--text2)', fontFamily: 'var(--font-mono)', fontSize: 10, cursor: 'pointer', letterSpacing: 1, textTransform: 'uppercase' }}>
              {m === 'build' ? '⚡ Build' : '💬 Discuss'}
            </button>
          ))}
        </div>

        <div style={{ position: 'relative' }}>
          <button onClick={() => setShowModelPicker(!showModelPicker)}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 6, color: 'var(--fire)', fontFamily: 'var(--font-mono)', fontSize: 10, cursor: 'pointer' }}>
            {currentModelInfo.icon} {currentModelInfo.label} ▾
          </button>
          {showModelPicker && (
            <div style={{ position: 'absolute', top: 32, right: 0, background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 8, zIndex: 100, minWidth: 220, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', maxHeight: 360, overflowY: 'auto' }}>
              {MODELS.map(m => (
                <div key={m.id} onClick={() => { setSelectedModel(m.id); setShowModelPicker(false) }}
                  style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid var(--border)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--panel)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span style={{ fontSize: 14 }}>{m.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: selectedModel === m.id ? 'var(--fire)' : 'var(--text)' }}>{m.label}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text2)' }}>{m.description}</div>
                  </div>
                  {selectedModel === m.id && <span style={{ color: 'var(--fire)', fontSize: 12 }}>✓</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        {codeOutput && (
          <>
            <button onClick={downloadZip} style={{ padding: '5px 12px', background: 'transparent', border: '1px solid var(--border2)', borderRadius: 6, color: 'var(--text2)', fontFamily: 'var(--font-mono)', fontSize: 10, cursor: 'pointer' }}>⬇ ZIP</button>
            <button onClick={() => setShowPublish(true)} style={{ padding: '5px 12px', background: 'var(--fire)', border: 'none', borderRadius: 6, color: '#fff', fontFamily: 'var(--font-mono)', fontSize: 10, cursor: 'pointer', letterSpacing: 1 }}>🚀 PUBLISH</button>
          </>
        )}

        {project?.published_url && (
          <a href={project.published_url} target="_blank" rel="noreferrer"
            style={{ padding: '5px 10px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, color: '#22c55e', fontFamily: 'var(--font-mono)', fontSize: 10, textDecoration: 'none' }}>
            ↗ LIVE
          </a>
        )}
      </div>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Left: Chat */}
        <div style={{ width: 380, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
            {messages.length === 0 && !loading && (
              <div style={{ textAlign: 'center', paddingTop: 60 }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🐉</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text2)', lineHeight: 1.7 }}>
                  {mode === 'discuss' ? "Tell me about your app idea.\nI'll help you plan it." : "Describe what you want to build.\nI'll generate it instantly."}
                </div>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div key={msg.id} style={{ marginBottom: 16 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: msg.role === 'user' ? 'var(--text2)' : 'var(--fire)', letterSpacing: 2, marginBottom: 4, textTransform: 'uppercase' }}>
                  {msg.role === 'user' ? 'YOU' : 'DRAGONCLAW'}
                  {msg.mode === 'discuss' && <span style={{ color: 'var(--text2)', marginLeft: 6 }}>· discuss</span>}
                </div>

                {msg.role === 'assistant' && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                    {/wrote|created|built/i.test(msg.content) && (
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', color: '#22c55e', borderRadius: 4, padding: '2px 6px' }}>📝 Wrote index.html</span>
                    )}
                    {/fixed|updated|changed|edited/i.test(msg.content) && (
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, background: 'rgba(255,180,0,0.1)', border: '1px solid rgba(255,180,0,0.2)', color: '#fbbf24', borderRadius: 4, padding: '2px 6px' }}>✏️ Edited index.html</span>
                    )}
                    {/added|adding/i.test(msg.content) && (
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)', color: '#818cf8', borderRadius: 4, padding: '2px 6px' }}>➕ Added feature</span>
                    )}
                  </div>
                )}

                <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', lineHeight: 1.6 }}>
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                </div>

                {msg.role === 'assistant' && idx === messages.length - 1 && suggestions.length > 0 && !loading && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text2)', letterSpacing: 1, marginBottom: 6 }}>SUGGESTIONS</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {suggestions.map((s, i) => (
                        <button key={i} onClick={() => { setInput(s); textareaRef.current?.focus() }}
                          style={{ padding: '5px 10px', background: 'var(--panel)', border: '1px solid var(--border2)', borderRadius: 20, color: 'var(--text2)', fontFamily: 'var(--font-mono)', fontSize: 10, cursor: 'pointer' }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--fire)'; e.currentTarget.style.color = 'var(--fire)' }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text2)' }}>
                          + {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            <BuildLog steps={buildSteps} isBuilding={loading} currentStatus={currentStatus} />
            {loading && <div ref={messagesEndRef} />}
            <div ref={messagesEndRef} />
          </div>

          <div style={{ padding: '12px', borderTop: '1px solid var(--border)', background: 'var(--surface)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text2)', display: 'flex', alignItems: 'center', gap: 5 }}>
                {loading ? (
                  <>
                    <span style={{ width: 6, height: 6, background: 'var(--fire)', borderRadius: '50%', display: 'inline-block', animation: 'blink 1s infinite' }} />
                    <span style={{ color: 'var(--fire)' }}>using</span>
                    <span style={{ color: 'var(--text)', background: 'rgba(255,80,0,0.1)', border: '1px solid rgba(255,80,0,0.2)', borderRadius: 4, padding: '1px 6px' }}>{activeModel || 'auto'}</span>
                  </>
                ) : (
                  <>
                    <span style={{ width: 6, height: 6, background: '#22c55e', borderRadius: '50%', display: 'inline-block' }} />
                    <span>ready</span>
                    {activeModel && <><span style={{ opacity: 0.4 }}>·</span><span style={{ background: 'var(--panel)', borderRadius: 4, padding: '1px 6px' }}>{activeModel}</span></>}
                  </>
                )}
              </div>
              {mode === 'discuss' && <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text2)' }}>💬 discuss</div>}
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <textarea ref={textareaRef} value={input}
                onChange={e => { setInput(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px' }}
                onKeyDown={handleKeyDown}
                placeholder={mode === 'discuss' ? 'Ask me about your app idea...' : 'Describe what to build or change...'}
                style={{ flex: 1, background: 'var(--panel)', border: '1px solid var(--border2)', borderRadius: 8, padding: '10px 12px', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: 13, resize: 'none', outline: 'none', lineHeight: 1.5 }}
                rows={1} disabled={loading} />
              <button onClick={() => sendMessage()} disabled={loading || !input.trim()}
                style={{ width: 38, height: 38, background: 'var(--fire)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 16, cursor: 'pointer', flexShrink: 0, opacity: !input.trim() ? 0.4 : 1 }}>
                ▶
              </button>
            </div>
            {loading && <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--fire)', marginTop: 6 }}>{currentStatus || 'Working...'}</div>}
          </div>
        </div>

        {/* Right panel */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ height: 40, borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', padding: '0 14px', gap: 4, background: 'var(--surface)' }}>
            {[
              { id: 'preview', label: '▶ PREVIEW' },
              { id: 'code', label: '</> CODE' },
              { id: 'versions', label: '🕐 VERSIONS' },
              { id: 'dashboard', label: '⚙ APP' },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                style={{ padding: '4px 12px', background: tab === t.id ? 'var(--panel)' : 'transparent', border: tab === t.id ? '1px solid var(--border2)' : '1px solid transparent', borderRadius: 6, color: tab === t.id ? 'var(--text)' : 'var(--text2)', fontFamily: 'var(--font-mono)', fontSize: 10, cursor: 'pointer', letterSpacing: 1 }}>
                {t.label}
              </button>
            ))}

            {tab === 'preview' && codeOutput && (
              <>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 2, background: 'var(--panel)', borderRadius: 6, padding: '2px 3px' }}>
                  {PREVIEW_SIZES.map(s => (
                    <button key={s.id} onClick={() => setPreviewSize(s.id)} title={s.label}
                      style={{ padding: '3px 8px', background: previewSize === s.id ? 'var(--surface)' : 'transparent', border: previewSize === s.id ? '1px solid var(--border2)' : '1px solid transparent', borderRadius: 4, color: previewSize === s.id ? 'var(--text)' : 'var(--text2)', fontSize: 12, cursor: 'pointer' }}>
                      {s.icon}
                    </button>
                  ))}
                </div>
                <button onClick={() => setPreviewKey(k => k + 1)}
                  style={{ padding: '4px 10px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text2)', fontFamily: 'var(--font-mono)', fontSize: 10, cursor: 'pointer' }}>↺</button>
              </>
            )}

            {codeOutput && tab === 'code' && (
              <button onClick={() => { navigator.clipboard.writeText(codeOutput); setCopied(true); setTimeout(() => setCopied(false), 2000) }}
                style={{ marginLeft: 'auto', padding: '4px 10px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, color: copied ? '#22c55e' : 'var(--text2)', fontFamily: 'var(--font-mono)', fontSize: 10, cursor: 'pointer' }}>
                {copied ? '✓ COPIED' : 'COPY'}
              </button>
            )}
          </div>

          {tab === 'preview' && (
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflow: 'auto', background: previewSize !== 'desktop' ? 'var(--panel)' : 'transparent', padding: previewSize !== 'desktop' ? '20px' : 0 }}>
              {codeOutput ? (
                <div style={{ width: currentPreviewSize.maxWidth, height: previewSize !== 'desktop' ? '700px' : '100%', transition: 'width 0.3s ease', boxShadow: previewSize !== 'desktop' ? '0 0 40px rgba(0,0,0,0.5)' : 'none', borderRadius: previewSize !== 'desktop' ? 12 : 0, overflow: 'hidden', flexShrink: 0 }}>
                  <iframe key={`${previewKey}-${previewSize}`} srcDoc={codeOutput} style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }} title="preview" sandbox="allow-scripts allow-forms allow-modals" />
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', gap: 12 }}>
                  <div style={{ fontSize: 48, opacity: 0.3 }}>🐉</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text2)', letterSpacing: 2 }}>PREVIEW APPEARS HERE</div>
                </div>
              )}
            </div>
          )}

          {tab === 'code' && (
            <div style={{ flex: 1, overflow: 'auto', background: 'var(--panel)' }}>
              {codeOutput
                ? <pre style={{ margin: 0, padding: 20, fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text)', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{codeOutput}</pre>
                : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text2)' }}>No code generated yet</div>
              }
            </div>
          )}

          {tab === 'versions' && (
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <VersionHistory projectId={projectId} onRestore={(content) => { setCodeOutput(content); setTab('preview'); setPreviewKey(k => k + 1) }} />
            </div>
          )}

          {tab === 'dashboard' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
              <div style={{ fontFamily: 'var(--font-disp)', fontSize: 20, color: 'var(--fire)', letterSpacing: 3, marginBottom: 24 }}>⚙ APP SETTINGS</div>

              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text2)', marginBottom: 8, letterSpacing: 1 }}>🌐 YOUR APP URL</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fire)', background: 'var(--panel)', border: '1px solid var(--border2)', borderRadius: 6, padding: '8px 12px' }}>{publishedUrl}</div>
                  <button onClick={() => { navigator.clipboard.writeText(publishedUrl); setUrlCopied(true); setTimeout(() => setUrlCopied(false), 2000) }}
                    style={{ padding: '8px 14px', background: 'var(--panel)', border: '1px solid var(--border2)', borderRadius: 6, color: urlCopied ? '#22c55e' : 'var(--text2)', fontFamily: 'var(--font-mono)', fontSize: 10, cursor: 'pointer' }}>
                    {urlCopied ? '✓' : 'COPY'}
                  </button>
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text2)', marginTop: 8 }}>Hosting coming soon · Upgrade to activate</div>
              </div>

              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text2)', marginBottom: 12, letterSpacing: 1 }}>👁 APP VISIBILITY</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['public', 'private'].map(v => (
                    <button key={v} onClick={toggleVisibility} disabled={savingVisibility}
                      style={{ padding: '8px 20px', background: visibility === v ? 'var(--fire)' : 'var(--panel)', border: `1px solid ${visibility === v ? 'var(--fire)' : 'var(--border2)'}`, borderRadius: 8, color: visibility === v ? '#fff' : 'var(--text2)', fontFamily: 'var(--font-mono)', fontSize: 11, cursor: 'pointer', textTransform: 'uppercase' }}>
                      {v === 'public' ? '🌍 Public' : '🔒 Private'}
                    </button>
                  ))}
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text2)', marginTop: 8 }}>
                  {visibility === 'public' ? 'Anyone with the link can view' : 'Only you can access this app'}
                </div>
              </div>

              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text2)', marginBottom: 12, letterSpacing: 1 }}>📦 EXPORT</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={downloadZip} disabled={!codeOutput}
                    style={{ padding: '8px 16px', background: 'var(--panel)', border: '1px solid var(--border2)', borderRadius: 8, color: 'var(--text)', fontFamily: 'var(--font-mono)', fontSize: 11, cursor: 'pointer', opacity: codeOutput ? 1 : 0.5 }}>
                    ⬇ Download ZIP
                  </button>
                  <button onClick={() => setShowPublish(true)} disabled={!codeOutput}
                    style={{ padding: '8px 16px', background: 'var(--fire)', border: 'none', borderRadius: 8, color: '#fff', fontFamily: 'var(--font-mono)', fontSize: 11, cursor: 'pointer', opacity: codeOutput ? 1 : 0.5 }}>
                    🚀 Publish App
                  </button>
                </div>
              </div>

              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, opacity: 0.6 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text2)', marginBottom: 8, letterSpacing: 1 }}>
                  🐙 GITHUB <span style={{ color: 'var(--fire)', marginLeft: 6 }}>COMING SOON</span>
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text2)' }}>Auto-push every build to your GitHub repo</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showPublish && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }} onClick={() => setShowPublish(false)}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 12, padding: 32, maxWidth: 480, width: '90%' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: 'var(--font-disp)', fontSize: 28, color: 'var(--fire)', letterSpacing: 3, marginBottom: 16 }}>🚀 PUBLISH</div>
            <div style={{ background: 'var(--panel)', border: '1px solid var(--border2)', borderRadius: 8, padding: '10px 14px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text2)', marginBottom: 4, letterSpacing: 1 }}>YOUR APP URL</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--fire)' }}>{publishedUrl}</div>
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, background: 'rgba(34,197,94,0.1)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 20, padding: '3px 8px' }}>
                {visibility === 'public' ? '🌍 PUBLIC' : '🔒 PRIVATE'}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button onClick={() => { downloadZip(); setShowPublish(false) }}
                style={{ padding: '12px', background: 'var(--fire)', border: 'none', borderRadius: 8, color: '#fff', fontFamily: 'var(--font-disp)', fontSize: 18, letterSpacing: 2, cursor: 'pointer' }}>
                ⬇ DOWNLOAD ZIP
              </button>
              <a href="https://app.netlify.com/drop" target="_blank" rel="noreferrer"
                style={{ padding: '12px', background: 'transparent', border: '1px solid var(--border2)', borderRadius: 8, color: 'var(--text)', fontFamily: 'var(--font-mono)', fontSize: 12, textAlign: 'center', textDecoration: 'none', display: 'block' }}>
                → Open Netlify Drop
              </a>
              <div style={{ padding: '12px', background: 'transparent', border: '1px solid var(--border2)', borderRadius: 8, fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text2)', textAlign: 'center' }}>
                🌐 dragon-claw.com hosting — coming soon
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
